import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { inboxFirestore, inboxStorage } from '../../services/firebase';
import { collection, doc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useSelector } from 'react-redux';
import { RootState } from '..';

export interface Attachment {
	name: string;
	mimeType: string;
	length: number;
	humanReadableLength: string;
	public: boolean;
	url: string;
	commentId: string;
	ticketId: string;
	id: string;
}

export interface AttachmentsState {
	attachmentByCommentId: Record<string, Attachment[]>;
	attachmentByTicketId: Record<string, Attachment[]>;
	loading: boolean;
	error: string | null;
}

const initialState: AttachmentsState = {
	attachmentByCommentId: {},
	attachmentByTicketId: {},
	loading: false,
	error: null,
};

export const uploadAttachments = createAsyncThunk(
	'attachments/uploadAttachment',
	async (
		{ ticketId, commentId, files }: { ticketId: string; commentId: string; files: File[] },
		thunkApi
	) => {
		try {
			const uploadedMetadata: any[] = [];

			for (const file of files) {
				const storageRef = ref(inboxStorage, `attachments/${ticketId}/${file.name}`);
				const snapshot = await uploadBytes(storageRef, file);
				const url = await getDownloadURL(snapshot.ref);

				const docRef = doc(collection(inboxFirestore, 'attachments'));

				const metadata = {
					id: docRef.id,
					ticketId,
					name: file.name,
					mimeType: file.type,
					length: file.size,
					humanReadableLength: `${(file.size / 1024).toFixed(2)} KB`,
					public: true,
					url,
					commentId,
				};

				await setDoc(docRef, metadata);
				uploadedMetadata.push(metadata);
			}

			return uploadedMetadata;
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const getAttachmentsByTicketId = createAsyncThunk(
	'attachments/getAttachmentsByTicketId',
	async (ticketId: string, thunkApi) => {
		try {
			const q = query(collection(inboxFirestore, 'attachments'), where('ticketId', '==', ticketId));
			const querySnapshot = await getDocs(q);
			const attachments: Attachment[] = [];
			querySnapshot.forEach((doc) => {
				const data = doc.data() as Attachment;
				if (!data.commentId) {
					attachments.push(data);
				}
			});

			return { ticketId, attachments };
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const getAttachmentsByCommentId = createAsyncThunk(
	'attachments/getAttachmentsByCommentId',
	async (commentId: string, thunkApi) => {
		try {
			const q = query(
				collection(inboxFirestore, 'attachments'),
				where('commentId', '==', commentId)
			);
			const querySnapshot = await getDocs(q);
			const attachments: Attachment[] = [];
			querySnapshot.forEach((doc) => {
				attachments.push(doc.data() as Attachment);
			});
			return { commentId, attachments };
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const attachmentsSlice = createSlice({
	name: 'attachments',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(uploadAttachments.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(uploadAttachments.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				action.payload.forEach((att) => {
					if (att.commentId) {
						const current = state.attachmentByCommentId[att.commentId] || [];
						state.attachmentByCommentId[att.commentId] = [...current, att];
					} else {
						const current = state.attachmentByTicketId[att.ticketId] || [];
						state.attachmentByTicketId[att.ticketId] = [...current, att];
					}
				});
			})
			.addCase(uploadAttachments.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(getAttachmentsByTicketId.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getAttachmentsByTicketId.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.attachmentByTicketId[action.payload.ticketId] = action.payload.attachments;
			})
			.addCase(getAttachmentsByCommentId.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getAttachmentsByCommentId.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.attachmentByCommentId[action.payload.commentId] = action.payload.attachments;
			});
	},
});

export const useAttachments = () =>
	useSelector<RootState, AttachmentsState>((state) => state.attachments);

export default attachmentsSlice.reducer;
